import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Collapse } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FiMoreVertical } from "react-icons/fi";
import { FaRegUser } from "react-icons/fa6";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  deleteAVehicleSegment,
  fetchOneVehicleSegment,
} from "../../../actions/management.action";
import { LogoutAlertDialog } from "../../../components/LogoutAlertDialog";
import { Loading } from "../../../components/Loading";
import useDeleteToast from "../../../components/DeleteToast";
import { DeleteAlert } from "../../../components/DeleteAlert";

const VehicleDetails = () => {
  const toast = useToast();
  const showDeleteToast = useDeleteToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });
  const [loading, setLoading] = useState(true);
  const [vehicleData, setVehicleData] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const onCloseAlert = () => setIsAlertOpen(false);
  const onOpenAlert = () => setIsAlertOpen(true);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const getVehicleDetails = async () => {
    try {
      let res = await fetchOneVehicleSegment(id);
      setVehicleData(res.data);
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteASegment = async () => {
    setDeleteConfirm({ isOpen: true });
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteAVehicleSegment(id);
      showDeleteToast("Vehicle Segment deleted successfully");
      navigate("/management/vehicles");
    } catch {
      toast({
        title: "Error",
        description: "Unable to delete Vehicle Segment",
        status: "error",
      });
    }
  };

  useEffect(() => {
    if (id) {
      getVehicleDetails();
    }
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!vehicleData) {
    return (
      <Box px={4} py={6}>
        <Text>Vehicle not found</Text>
      </Box>
    );
  }

  const pricing = vehicleData?.pricing || {};

  const premiumData = pricing.outstationOneWay?.premium?.isPremium
    ? pricing.outstationOneWay.premium
    : pricing.outstationRoundTrip?.premium?.isPremium
      ? pricing.outstationRoundTrip.premium
      : pricing.airportTransfer?.premium?.isPremium
        ? pricing.airportTransfer.premium
        : null;

  const discountData = pricing.outstationOneWay?.discount?.isDiscountApply
    ? pricing.outstationOneWay.discount
    : pricing.outstationRoundTrip?.discount?.isDiscountApply
      ? pricing.outstationRoundTrip.discount
      : pricing.airportTransfer?.discount?.isDiscountApply
        ? pricing.airportTransfer.discount
        : null;

  const surchargeData = pricing.outstationOneWay?.surcharge?.isSurchargeApply
    ? pricing.outstationOneWay.surcharge
    : pricing.outstationRoundTrip?.surcharge?.isSurchargeApply
      ? pricing.outstationRoundTrip.surcharge
      : pricing.airportTransfer?.surcharge?.isSurchargeApply
        ? pricing.airportTransfer.surcharge
        : null;

  const getPricingExtras = (pricingBlock) => {
    const premium =
      pricingBlock?.premium?.isPremium &&
      Number(pricingBlock?.premium?.premiumSurcharge) > 0
        ? pricingBlock.premium
        : null;

    const discount =
      pricingBlock?.discount?.isDiscountApply &&
      Number(pricingBlock?.discount?.discount) > 0
        ? pricingBlock.discount
        : null;

    const surcharge =
      pricingBlock?.surcharge?.isSurchargeApply &&
      Number(pricingBlock?.surcharge?.amount) > 0
        ? pricingBlock.surcharge
        : null;

    return { premium, discount, surcharge };
  };

  const PricingExtrasBadges = ({ block }) => {
    const { premium, discount, surcharge } = getPricingExtras(block);

    if (!premium && !discount && !surcharge) return null;

    return (
      <Flex flexWrap="wrap" gap={2} mt={3}>
        {premium && (
          <Badge colorScheme="purple">
            Premium +₹{premium.premiumSurcharge}
          </Badge>
        )}

        {discount && (
          <Badge colorScheme="green">{discount.discount}% Discount</Badge>
        )}

        {surcharge && (
          <Badge colorScheme="orange">+₹{surcharge.amount} Surcharge</Badge>
        )}
      </Flex>
    );
  };

  return (
    <>
      <DeleteAlert
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false })}
        onDelete={onDeleteConfirm}
        label="Vehicle"
        itemName="Vehicle"
      />
      <Box px={4} py={6}>
        <Flex
          justifyContent={{ base: "center", md: "space-between" }}
          alignItems={{ base: "flex-start", md: "center" }}
          mb={6}
          flexDir={{ base: "column", md: "row" }}
        >
          <BreadCrumbMaker
            items={[
              { link: "/management", title: "Operations Setup" },
              { link: "/management/vehicles", title: "Vehicles" },
              {
                link: `/management/vehicles/${id}`,
                title: vehicleData?.segmentName || "Details",
                on: true,
              },
            ]}
          />

          <Flex gap={2}>
            <Button
              as={Link}
              to={`/management/vehicles/edit/${id}`}
              colorScheme="blue"
              size="sm"
              borderRadius={5}
              px={10}
              fontWeight={0}
              py={5}
            >
              Edit Vehicle
            </Button>
          </Flex>
        </Flex>

        {/* Content Section */}
        <Box p={4} bg={"white"} overflowX={"auto"} borderRadius={"lg"}>
          {/* Main Vehicle Info Card */}
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={4}
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              borderBottomWidth={{ base: "0", md: "2px" }}
              borderBottomColor={{ base: "transparent", md: "gray.200" }}
              pb={4}
            >
              <Flex
                gap={4}
                flexDirection={{ base: "column", md: "row" }}
                mb={4}
                alignItems={{ base: "stretch", md: "center" }}
              >
                <Image
                  src={
                    vehicleData?.images?.length > 0
                      ? vehicleData?.images?.[0]
                      : "/images.png"
                  }
                  alt={vehicleData?.segmentName}
                  w="150px"
                  h="100px"
                  rounded="md"
                />
                <VStack alignItems={"stretch"}>
                  <Heading fontSize="lg" mb={1} color={"#1A202C"}>
                    {vehicleData?.segmentName}
                  </Heading>
                  <Text fontSize="sm" color={"gray.600"} mb={2}>
                    {vehicleData?.description}
                  </Text>
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    gap={3}
                    flexWrap="wrap"
                  >
                    <Badge variant="subtle" colorScheme="orange" size={"sm"}>
                      {vehicleData?.vehicleTypes || "Economy"}
                    </Badge>
                    <Flex justifyContent={"center"} alignItems={"center"}>
                      <FaRegUser />
                      <Text fontSize={"md"} px={2}>
                        {vehicleData?.passengerCapacity} Seats
                      </Text>
                    </Flex>
                    <Badge
                      variant="subtle"
                      colorScheme={
                        vehicleData?.status === "active" ? "green" : "red"
                      }
                    >
                      {vehicleData?.status}
                    </Badge>
                  </Flex>
                </VStack>
              </Flex>

              {/* Action Button */}
              <Menu>
                <MenuButton
                  as={Button}
                  aria-label="More Options"
                  variant="ghost"
                  size="sm"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={2}
                >
                  <FiMoreVertical />
                </MenuButton>
                <MenuList
                  minW="150px"
                  p={2}
                  gap={2}
                  borderRadius={0}
                  boxShadow={"lg"}
                >
                  <Link
                    to={`/management/vehicles/edit/${id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      _hover={{
                        bg: "gray.100",
                        color: "blue.500",
                        cursor: "pointer",
                      }}
                      p={2}
                      borderRadius={0}
                      transition="background-color 0.2s ease"
                    >
                      Edit
                    </Box>
                  </Link>
                  <Box
                    _hover={{
                      bg: "gray.100",
                      color: "red.500",
                      cursor: "pointer",
                    }}
                    p={2}
                    borderRadius={0}
                    transition="background-color 0.2s ease"
                  >
                    <LogoutAlertDialog
                      isOpen={isAlertOpen}
                      onOpen={onOpenAlert}
                      callback={deleteASegment}
                      onClose={onCloseAlert}
                      title={"Delete"}
                      heading={"Do you want to delete this vehicle segment?"}
                      buttonViewContent={<Text>Delete</Text>}
                    />
                  </Box>
                </MenuList>
              </Menu>
            </Flex>

            <Flex flexWrap={"wrap"} gap={4} mt={4}>
              <Box fontSize={"small"} p={2}>
                <Text
                  fontWeight={"bold"}
                  mb={3}
                  fontSize="md"
                  color={"#1A202C"}
                >
                  Features
                </Text>
                {vehicleData?.amenities?.map((feat, index) => (
                  <Text key={index} fontSize="sm" color={"#1A202C"}>
                    - {feat}
                  </Text>
                ))}
              </Box>
              <Box fontSize={"small"} p={2}>
                <Text
                  fontWeight={"bold"}
                  mb={3}
                  fontSize="md"
                  color={"#1A202C"}
                >
                  Fuels
                </Text>
                {vehicleData?.fuelTypes?.map((fuel, index) => (
                  <Text key={index} fontSize="sm" color={"#1A202C"}>
                    - {fuel}
                  </Text>
                ))}
              </Box>
              <Box fontSize={"small"} p={2}>
                <Text
                  fontWeight={"bold"}
                  mb={3}
                  fontSize="md"
                  color={"#1A202C"}
                >
                  Luggage Capacity
                </Text>
                <Text fontSize="sm" color={"#1A202C"}>
                  {vehicleData?.luggageCapacity} Liters
                </Text>
              </Box>
              {/* <Box fontSize={"small"} p={2}>
              <Text fontWeight={"bold"} mb={3} fontSize="md" color={"#1A202C"}>
                Availability
              </Text>
              <Badge
                colorScheme={vehicleData?.availablity ? "green" : "red"}
              >
                {vehicleData?.availablity ? "Available" : "Not Available"}
              </Badge>
            </Box> */}

              {/* Inclusions */}
              {vehicleData?.inclusions?.length > 0 && (
                <Box fontSize={"small"} p={2}>
                  <Text
                    fontWeight={"bold"}
                    mb={3}
                    fontSize="md"
                    color={"#10B981"}
                  >
                    ✓ Inclusions
                  </Text>
                  {vehicleData.inclusions
                    .filter((item) => item.checked)
                    .map((item, index) => (
                      <Text key={index} fontSize="sm" color={"#1A202C"}>
                        - {item.label}
                      </Text>
                    ))}
                </Box>
              )}

              {/* Exclusions */}
              {vehicleData?.exclusions?.length > 0 && (
                <Box fontSize={"small"} p={2}>
                  <Text
                    fontWeight={"bold"}
                    mb={3}
                    fontSize="md"
                    color={"#EF4444"}
                  >
                    ✗ Exclusions
                  </Text>
                  {vehicleData.exclusions
                    .filter((item) => item.checked)
                    .map((item, index) => (
                      <Text key={index} fontSize="sm" color={"#1A202C"}>
                        - {item.label}
                      </Text>
                    ))}
                </Box>
              )}
            </Flex>
          </Box>

          {/* Pricing Grid */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={4}
            mb={4}
          >
            {/* Outstation One Way */}
            <Box
              border="2px"
              borderColor="gray.200"
              p={4}
              shadow="sm"
              bg="white"
              borderRadius="lg"
              transition="all 0.2s ease"
              _hover={{ shadow: "md", transform: "translateY(-2px)" }}
            >
              <Box
                mb={3}
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                {/* Title Row */}
                <Flex justify="space-between" align="center">
                  <Heading fontSize="sm" color="#1A202C">
                    One Way
                  </Heading>

                  <Badge colorScheme="blue" fontSize="xs">
                    Outstation
                  </Badge>
                </Flex>

                {/* Tags Row */}
                <PricingExtrasBadges
                  block={vehicleData?.pricing?.outstationOneWay}
                />
              </Box>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Tbody>
                    {[
                      [
                        "Per Km Rate",
                        `₹${vehicleData?.pricing?.outstationOneWay?.perKmRate}`,
                      ],
                      [
                        "Extra Km Rate",
                        `₹${vehicleData?.pricing?.outstationOneWay?.extraKmRate}`,
                      ],
                      [
                        "Driver Charge",
                        `₹${vehicleData?.pricing?.outstationOneWay?.driverCharge}`,
                      ],
                      [
                        "Night Charge",
                        `₹${vehicleData?.pricing?.outstationOneWay?.nightCharge}`,
                      ],
                      [
                        "Waiting Charge",
                        `₹${vehicleData?.pricing?.outstationOneWay?.waitingCharge}`,
                      ],
                      [
                        "Toll Charge",
                        `₹${vehicleData?.pricing?.outstationOneWay?.tollCharge}`,
                      ],
                      [
                        "Min Km / Day",
                        `${vehicleData?.pricing?.outstationOneWay?.minimumKmPerDay} km`,
                      ],
                    ].map(([label, value], i) => (
                      <Tr key={i}>
                        <Td fontWeight="medium">{label}</Td>
                        <Td textAlign="right" color="gray.600">
                          {value || "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Outstation Round Trip */}
            <Box
              border="2px"
              borderColor="gray.200"
              p={4}
              shadow="sm"
              bg="white"
              borderRadius="lg"
              transition="all 0.2s ease"
              _hover={{ shadow: "md", transform: "translateY(-2px)" }}
            >
              <Box
                mb={3}
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Flex justify="space-between" align="center">
                  <Heading fontSize="sm" color="#1A202C">
                    Round Trip
                  </Heading>
                  <Badge colorScheme="purple" fontSize="xs">
                    Outstation
                  </Badge>
                </Flex>

                <PricingExtrasBadges
                  block={vehicleData?.pricing?.outstationRoundTrip}
                />
              </Box>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Tbody>
                    {[
                      [
                        "Per Km Rate",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.perKmRate}`,
                      ],
                      [
                        "Extra Km Rate",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.extraKmRate}`,
                      ],
                      [
                        "Driver Charge",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.driverCharge}`,
                      ],
                      [
                        "Night Charge",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.nightCharge}`,
                      ],
                      [
                        "Waiting Charge",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.waitingCharge}`,
                      ],
                      [
                        "Toll Charge",
                        `₹${vehicleData?.pricing?.outstationRoundTrip?.tollCharge}`,
                      ],
                      [
                        "Min Km / Day",
                        `${vehicleData?.pricing?.outstationRoundTrip?.minimumKmPerDay} km`,
                      ],
                    ].map(([label, value], i) => (
                      <Tr key={i}>
                        <Td fontWeight="medium">{label}</Td>
                        <Td textAlign="right" color="gray.600">
                          {value || "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Airport Transfer */}
            <Box
              border="2px"
              borderColor="gray.200"
              p={4}
              shadow="sm"
              bg="white"
              borderRadius="lg"
              transition="all 0.2s ease"
              _hover={{ shadow: "md", transform: "translateY(-2px)" }}
            >
              <Box
                mb={3}
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Flex justify="space-between" align="center">
                  <Heading fontSize="sm" color="#1A202C">
                    Airport Transfer
                  </Heading>
                  <Badge colorScheme="green" fontSize="xs">
                    Airport
                  </Badge>
                </Flex>

                <PricingExtrasBadges
                  block={vehicleData?.pricing?.airportTransfer}
                />
              </Box>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Tbody>
                    {[
                      [
                        "Per Km Rate",
                        `₹${vehicleData?.pricing?.airportTransfer?.perKmRate}`,
                      ],
                      [
                        "Extra Km Rate",
                        `₹${vehicleData?.pricing?.airportTransfer?.extraKmRate}`,
                      ],
                      [
                        "Driver Charge",
                        `₹${vehicleData?.pricing?.airportTransfer?.driverCharge}`,
                      ],
                      [
                        "Night Charge",
                        `₹${vehicleData?.pricing?.airportTransfer?.nightCharge}`,
                      ],
                      [
                        "Waiting Charge",
                        `₹${vehicleData?.pricing?.airportTransfer?.waitingCharge}`,
                      ],
                      [
                        "Toll Charge",
                        `₹${vehicleData?.pricing?.airportTransfer?.tollCharge}`,
                      ],
                    ].map(([label, value], i) => (
                      <Tr key={i}>
                        <Td fontWeight="medium">{label}</Td>
                        <Td textAlign="right" color="gray.600">
                          {value || "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>

          {/* Pricing Section - Local Rental */}
          {vehicleData?.pricing?.localRental?.length > 0 && (
            <Box
              border="2px"
              borderColor="gray.200"
              p={4}
              shadow="sm"
              bg="white"
              borderRadius="lg"
              mb={4}
            >
              <Heading fontSize="md" mb={4} color="#1A202C">
                Local Rental Pricing
              </Heading>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                {vehicleData.pricing.localRental.map((rental, index) => (
                  <Box
                    key={rental._id || index}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    p={4}
                    shadow="sm"
                    transition="all 0.2s ease"
                    _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                    bg="gray.50"
                  >
                    {/* Card Header */}
                    <Box
                      mb={3}
                      pb={2}
                      borderBottom="1px solid"
                      borderColor="gray.300"
                    >
                      <Flex justify="space-between" align="center">
                        <Text fontWeight="bold" color="blue.600">
                          Package {index + 1}
                        </Text>

                        <Badge colorScheme="teal" fontSize="xs">
                          {rental.durationHours}h
                        </Badge>
                      </Flex>

                      <PricingExtrasBadges block={rental} />
                    </Box>

                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Tbody>
                          {[
                            ["Included Km", `${rental.includedKm} km`],
                            ["Price", `₹${rental.price}`],
                            ["Extra Km", `₹${rental.extraKmRate}`],
                            ["Extra Hour", `₹${rental.extraHourRate}`],
                          ].map(([label, value], i) => (
                            <Tr key={i}>
                              <Td fontWeight="medium">{label}</Td>
                              <Td textAlign="right" color="gray.600">
                                {value || "-"}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Grid>
            </Box>
          )}

          {/* Premium Section */}
          {/* <Box
          border="2px"
          borderColor="gray.200"
          p={4}
          shadow="sm"
          bg="white"
          borderRadius="lg"
          mb={4}
        >
          <Heading fontSize="md" mb={4} color="#1A202C">
            Premium Features
          </Heading>

          <Flex flexWrap="wrap" gap={4}>
            <Box>
              <Text fontWeight="bold" mb={2}>
                Premium Status
              </Text>
              <Badge colorScheme={premiumData ? "purple" : "gray"}>
                {premiumData ? "Premium" : "Standard"}
              </Badge>
            </Box>

            {premiumData && (
              <>
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Premium Surcharge
                  </Text>
                  <Text>₹{premiumData.premiumSurcharge}</Text>
                </Box>

                {premiumData.addons?.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Addons
                    </Text>
                    {premiumData.addons.map((addon, index) => (
                      <Badge key={index} mr={2} colorScheme="purple">
                        {addon}
                      </Badge>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Flex>
        </Box> */}

          {/* Discount & Surcharge Section */}
          {/* <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}> */}
          {/* Discount */}
          {/* <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg="white"
            borderRadius="lg"
          >
            <Heading fontSize="md" mb={4} color="#1A202C">
              Discount
            </Heading>

            <VStack alignItems="stretch" spacing={3}>
              <Flex justifyContent="space-between">
                <Text fontWeight="bold">Discount Applied</Text>
                <Badge colorScheme={discountData ? "green" : "gray"}>
                  {discountData ? "Yes" : "No"}
                </Badge>
              </Flex>

              {discountData && (
                <>
                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">Discount Percentage</Text>
                    <Text>{discountData.discount}%</Text>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">Start Date</Text>
                    <Text>
                      {discountData.startDate
                        ? new Date(discountData.startDate).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">End Date</Text>
                    <Text>
                      {discountData.endDate
                        ? new Date(discountData.endDate).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </Flex>
                </>
              )}
            </VStack>
          </Box> */}

          {/* Surcharge */}
          {/* <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg="white"
            borderRadius="lg"
          >
            <Heading fontSize="md" mb={4} color="#1A202C">
              Surcharge
            </Heading>

            <VStack alignItems="stretch" spacing={3}>
              <Flex justifyContent="space-between">
                <Text fontWeight="bold">Surcharge Applied</Text>
                <Badge colorScheme={surchargeData ? "orange" : "gray"}>
                  {surchargeData ? "Yes" : "No"}
                </Badge>
              </Flex>

              {surchargeData && (
                <>
                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">Surcharge Amount</Text>
                    <Text>₹{surchargeData.amount}</Text>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">Start Date</Text>
                    <Text>
                      {surchargeData.startDate
                        ? new Date(surchargeData.startDate).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </Flex>

                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">End Date</Text>
                    <Text>
                      {surchargeData.endDate
                        ? new Date(surchargeData.endDate).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </Flex>
                </>
              )}
            </VStack>
          </Box> */}
          {/* </Grid> */}
          {/* Terms & Conditions Section */}
          {vehicleData?.termsAndConditions?.length > 0 && (
            <Box
              border="2px"
              borderColor="gray.200"
              p={4}
              shadow="sm"
              bg="white"
              borderRadius="lg"
              mt={4}
            >
              {/* Header */}
              <Flex
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={() => setIsTermsOpen(!isTermsOpen)}
              >
                <Heading fontSize="md" color="#1A202C">
                  Terms & Conditions
                </Heading>

                <Button
                  size="sm"
                  variant="ghost"
                  rightIcon={
                    isTermsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                  }
                >
                  {isTermsOpen ? "Hide" : "Show"}
                </Button>
              </Flex>

              {/* Dropdown Content */}
              <Collapse in={isTermsOpen} animateOpacity>
                <VStack align="stretch" spacing={3} mt={4}>
                  {vehicleData.termsAndConditions.map((label, index) => (
                    <Flex key={index} align="flex-start" gap={3}>
                      <Box
                        mt="6px"
                        minW="8px"
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="#3B82F6"
                      />
                      <Text fontSize="sm" color="#4B5563" lineHeight="1.6">
                        {label}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default VehicleDetails;
